"use client";
import { logger } from "@/utils/logger";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";

import { ChevronLeft, ChevronRight, Quote, Target, TrendingUp, ShieldCheck, Activity, Lightbulb } from "lucide-react";
import { EmptyState } from "@/components/ui/EmptyState";
import { apiClient } from "@/lib/apiClient";
import { useRouter } from "next/navigation";

export default function InsightsPage() {
  const router = useRouter();
  const [activeCategory, setActiveCategory] = useState("All");
  const [insightsData, setInsightsData] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>(["All"]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const blob3Ref = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const portraitRef = useRef<HTMLDivElement>(null);
  const promiseCardRef = useRef<HTMLDivElement>(null);
  const iconRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await apiClient.get('/public/blogs') as any;
        const blogs = response.data || [];

        // Map CMS data to frontend format
        const mappedData = blogs.map((blog: any) => ({
          id: blog._id,
          slug: blog.slug,
          category: blog.category || "General",
          date: blog.publishDate ? new Date(blog.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "Recently",
          title: blog.title,
          desc: (blog.excerpt || blog.summary || blog.content || "")
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/&nbsp;/gi, ' ')
            .replace(/<[^>]+>/g, ' ')
            .trim(),
          image: blog.featuredImage || blog.imageUrl || "/hero_consultancy.png", // fallback image
          authorName: blog.author || "Compare All",
          authorRole: "Consultant", // Default role
          authorAvatar: "/team_avatar_1.png", // Default avatar
        }));

        setInsightsData(mappedData);
        setCategories(["All", ...Array.from(new Set(mappedData.map((m: any) => m.category))) as string[]]);
      } catch (error) {
        logger.error("Failed to fetch insights", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const filteredInsights = activeCategory === "All" ? insightsData : insightsData.filter(item => item.category === activeCategory);
  const totalPages = Math.ceil(filteredInsights.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentItems = filteredInsights.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, 4, '...', totalPages];
    if (currentPage >= totalPages - 2) return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  useEffect(() => {
    Promise.all([
      import("gsap"),
      import("gsap/ScrollTrigger")
    ]).then(([gsapModule, scrollTriggerModule]) => {
      const gsap = gsapModule.default;
      const ScrollTrigger = scrollTriggerModule.ScrollTrigger;
      gsap.registerPlugin(ScrollTrigger);

      // 1. GSAP Magnetic Hover Effect for Icons
      iconRefs.current.forEach((icon) => {
        if (!icon) return;
        const handleMouseMove = (e: MouseEvent) => {
          const rect = icon.getBoundingClientRect();
          const x = e.clientX - rect.left - rect.width / 2;
          const y = e.clientY - rect.top - rect.height / 2;
          gsap.to(icon, { x: x * 0.4, y: y * 0.4, duration: 0.3, ease: "power2.out" });
        };
        const handleMouseLeave = () => {
          gsap.to(icon, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.3)" });
        };

        icon.addEventListener("mousemove", handleMouseMove);
        icon.addEventListener("mouseleave", handleMouseLeave);
      });

      // 2. Line Drawing Effect with GSAP
      gsap.fromTo('.promise-line path',
        { strokeDashoffset: 1500 },
        { strokeDashoffset: 0, duration: 3, stagger: 0.25, yoyo: true, repeat: -1, ease: "power1.inOut" }
      );

      // Floating blobs
      if (blob1Ref.current) gsap.to(blob1Ref.current, { y: -15, duration: 6, yoyo: true, repeat: -1, ease: "sine.inOut" });
      if (blob2Ref.current) gsap.to(blob2Ref.current, { y: 15, duration: 7, yoyo: true, repeat: -1, ease: "sine.inOut" });
      if (blob3Ref.current) gsap.to(blob3Ref.current, { y: -8, x: 5, duration: 5, yoyo: true, repeat: -1, ease: "sine.inOut" });

      // Filter bar
      if (filterRef.current) gsap.fromTo(filterRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.6, delay: 0.2, ease: "power2.out" });

      // Portrait scroll
      if (portraitRef.current) {
        gsap.fromTo(portraitRef.current,
          { opacity: 0, scale: 0.8, x: -20 },
          { opacity: 1, scale: 1, x: 0, duration: 0.8, delay: 0.2, ease: "elastic.out(1, 0.5)", scrollTrigger: { trigger: portraitRef.current, once: true, start: "top 85%" } }
        );
      }
    });

    return () => {
      iconRefs.current.forEach((icon) => {
        // We can't easily remove listeners here if they are scoped inside the promise, 
        // but normally this is okay since the component unmounts.
      });
    };
  }, []);

  // Post items animation
  useEffect(() => {
    let ctx: any;
    if (!loading && currentItems.length > 0) {
      import("gsap").then(({ default: gsap }) => {
        ctx = gsap.context(() => {
          gsap.fromTo(".featured-post",
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.6, ease: "power2.out", overwrite: "auto" }
          );
          gsap.utils.toArray(".stacked-post").forEach((el: any, idx) => {
            gsap.fromTo(el,
              { opacity: 0, x: 20 },
              { opacity: 1, x: 0, duration: 0.5, delay: idx * 0.1, ease: "power2.out", overwrite: "auto" }
            );
          });
        });
      });
      return () => { if (ctx) ctx.revert(); };
    }
  }, [loading, activeCategory, currentPage, currentItems]);

  return (
    <div className="flex flex-col min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden pt-36 md:pt-48 pb-24 relative">

      {/* ══════════════════════════════════════════
          1. CREATIVE HAND-DRAWN HERO
      ══════════════════════════════════════════ */}
      <section className="w-full relative min-h-[450px] flex flex-col items-center justify-center text-center px-6 mb-12">

        {/* Floating Left: Hand-drawn Business Chart */}
        <div
          ref={blob1Ref}
          className="absolute left-10 lg:left-32 top-1/2 -translate-y-1/2 hidden md:block"
        >
          <svg width="140" height="120" viewBox="0 0 100 100" fill="none" stroke="#0d9488" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="opacity-30">
            <path d="M10,90 Q12,50 10,10 M10,90 Q50,92 90,90" />
            <path d="M15,75 Q30,50 45,60 T85,20" />
            <path d="M85,20 L80,25 M85,20 L80,15" />
            <path d="M45,60 Q50,75 55,90 M25,65 Q28,80 30,90" strokeDasharray="2 4" />
          </svg>
        </div>

        {/* Floating Right: Hand-drawn Network / Brainstorm */}
        <div
          ref={blob2Ref}
          className="absolute right-10 lg:right-32 top-[30%] hidden md:block"
        >
          <svg width="110" height="110" viewBox="0 0 100 100" fill="none" stroke="#0d9488" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="opacity-30">
            <circle cx="30" cy="30" r="4" />
            <circle cx="70" cy="20" r="6" />
            <circle cx="50" cy="70" r="8" />
            <circle cx="85" cy="65" r="3" />
            <path d="M33,28 Q50,22 64,21 M68,26 Q60,50 54,63 M32,34 Q40,55 46,64 M58,70 Q70,72 82,66" />
          </svg>
        </div>

        {/* Floating Birds Equivalent (Top Right) */}
        <div
          ref={blob3Ref}
          className="absolute right-[25%] top-[10%] hidden lg:block"
        >
          <svg width="60" height="40" viewBox="0 0 100 100" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-40">
            <path d="M10,50 Q30,30 50,50 Q70,30 90,50" />
            <path d="M40,20 Q50,10 60,20 Q70,10 80,20" />
          </svg>
        </div>

        {/* Central Content */}
        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center">

          <div className="relative mb-10">
            <h1 className="text-[40px] md:text-[60px] font-serif font-extrabold tracking-tight text-gray-900">
              Insights & Research
            </h1>
            <svg className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-48 md:w-64 h-5 text-[#0d9488]" viewBox="0 0 100 10" preserveAspectRatio="none">
              <path d="M 5 5 Q 50 2 95 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M 10 8 Q 50 5 90 8" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.6" />
            </svg>
          </div>

          <p className="font-sans text-gray-500 text-[14px] md:text-[15px] max-w-2xl leading-relaxed font-medium mb-8">
            At Compare All Ltd, we specialize in requirement analysis, audit reports, and bespoke service offerings. Whether you need finance options, fleet management, or complete end-to-end management, we provide unbiased recommendations to drastically improve operational efficiency.
          </p>

          {/* Trust Indicators (Based on consultancy value, no duplication) */}
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12 mt-4">

            <div className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
              <ShieldCheck className="w-4 h-4 text-[#0d9488]" />
              <span className="text-[12px] font-semibold text-gray-600 tracking-wide">Unbiased Analysis</span>
            </div>

            <div className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
              <TrendingUp className="w-4 h-4 text-[#0d9488]" />
              <span className="text-[12px] font-semibold text-gray-600 tracking-wide">End-to-End Management</span>
            </div>

            <div className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
              <Target className="w-4 h-4 text-[#0d9488]" />
              <span className="text-[12px] font-semibold text-gray-600 tracking-wide">Bespoke Offerings</span>
            </div>

          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════
          2. MINIMAL CATEGORY NAVIGATION (Small Text)
      ══════════════════════════════════════════ */}
      <section className="w-full max-w-[1200px] mx-auto px-6 mb-16 relative z-10">
        <div
          ref={filterRef}
          className="flex flex-row flex-wrap items-center gap-6 md:gap-10 w-full opacity-0"
        >
          {categories.map((cat, i) => (
            <button
              key={i}
              onClick={() => { setActiveCategory(cat); setCurrentPage(1); }}
              className={`relative pb-2 text-[10px] md:text-[11px] font-bold tracking-[0.1em] uppercase transition-colors duration-300 ${activeCategory === cat
                ? "text-gray-900"
                : "text-gray-400 hover:text-gray-600"
                }`}
            >
              {cat}
              <div
                className={`absolute bottom-0 left-0 w-full h-[2px] bg-[#0d9488] transition-all duration-300 ${activeCategory === cat ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}
              />
            </button>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════
          3. CLEAN ASYMMETRIC LAYOUT (No Cards)
      ══════════════════════════════════════════ */}
      <section className="w-full max-w-[1200px] mx-auto px-6 relative z-10 min-h-[500px]">
        {loading ? (
          <div className="w-full flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#0d9488] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : currentItems.length > 0 ? (
          <div className="flex flex-col lg:flex-row gap-10">

            {/* Featured Post (Left Side) */}
            <div
              className="featured-post opacity-0 w-full lg:w-[60%] flex flex-col gap-6 group cursor-pointer"
              onClick={() => router.push(`/insights/${currentItems[0].slug}`)}
            >
              <div className="relative w-full h-[300px] sm:h-[450px] lg:h-[500px] rounded-[30px] overflow-hidden bg-gray-100">
                <Image
                  src={currentItems[0].image}
                  alt={currentItems[0].title}
                  fill
                  priority
                  className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md px-4 py-1.5 rounded-full shadow-sm">
                  <span className="text-[#0d9488] font-bold text-[10px] uppercase tracking-widest">{currentItems[0].category}</span>
                </div>
              </div>

              <div className="flex flex-col gap-3 pr-6">
                <span className="text-gray-400 font-medium text-[12px]">{currentItems[0].date}</span>
                <h2 className="text-[28px] md:text-[36px] font-bold text-gray-900 leading-tight group-hover:text-[#0d9488] transition-colors line-clamp-3">
                  {currentItems[0].title}
                </h2>
                <p className="text-gray-500 text-[14px] md:text-[16px] leading-relaxed line-clamp-3 mt-1">
                  {currentItems[0].desc}
                </p>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <Image src={currentItems[0].authorAvatar} alt="Author" width={32} height={32} className="rounded-full object-cover grayscale opacity-80" />
                <div>
                  <div className="text-gray-900 text-[12px] font-bold">{currentItems[0].authorName}</div>
                </div>
              </div>
            </div>

            {/* Stacked Smaller Posts (Right Side) */}
            <div className="w-full lg:w-[40%] flex flex-col gap-10">
              {currentItems.slice(1).map((item, i) => (
                <div
                  key={item.id}
                  className="stacked-post opacity-0 flex flex-col sm:flex-row lg:flex-col gap-5 group cursor-pointer border-b border-gray-100 lg:border-none pb-6 lg:pb-0 last:border-none last:pb-0"
                  onClick={() => router.push(`/insights/${item.slug}`)}
                >
                  {/* Small thumbnail for list items */}
                  <div className="relative w-full sm:w-[200px] lg:w-full h-[180px] sm:h-auto lg:h-[220px] rounded-lg overflow-hidden shrink-0 bg-gray-100">
                    <Image
                      src={item.image}
                      alt={item.title}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 200px, 40vw"
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>

                  <div className="flex flex-col py-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#0d9488] font-bold text-[9px] uppercase tracking-wider">{item.category}</span>
                      <span className="text-gray-400 text-[10px]">{item.date}</span>
                    </div>
                    <h3 className="text-[18px] font-bold text-gray-900 leading-snug mb-2 group-hover:text-[#0d9488] transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-[13px] leading-relaxed line-clamp-2">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        ) : (
          <div className="w-full py-12 border-t border-gray-100">
            <EmptyState
              title="Fresh Insights Coming Soon"
              message="We are currently crafting new, high-value consultancy insights. Check back shortly."
              icon={<Lightbulb className="w-8 h-8 text-teal-600 opacity-80" strokeWidth={1.5} />}
            />
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════
          4. PROFESSIONAL PAGINATION
      ══════════════════════════════════════════ */}
      {filteredInsights.length > 0 && (
        <section className="w-full max-w-[1200px] mx-auto px-6 mt-20">
          <div className="flex items-center justify-between border-t border-gray-200 pt-8">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`flex items-center gap-2 text-[12px] font-semibold transition-colors uppercase tracking-wider ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-900'}`}
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>

            <div className="hidden sm:flex items-center gap-2">
              {getPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === 'number' && setCurrentPage(page)}
                  disabled={page === '...'}
                  className={`w-8 h-8 flex items-center justify-center rounded-md text-[13px] font-medium transition-colors ${page === currentPage
                    ? "bg-gray-900 text-white shadow-sm"
                    : typeof page === 'number'
                      ? "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                      : "text-gray-400 cursor-default pointer-events-none"
                    }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`flex items-center gap-2 text-[12px] font-semibold transition-colors uppercase tracking-wider ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-[#0d9488] hover:text-teal-700'}`}
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════
          5. THE COMPARE ALL PROMISE (Curved Layout)
      ══════════════════════════════════════════ */}
      <section className="w-full relative flex flex-col lg:block overflow-hidden bg-gray-50 lg:min-h-[600px] mt-32">

        {/* Massive Background Image (Top on mobile, Left on desktop) */}
        <div className="relative w-full h-[300px] sm:h-[400px] lg:absolute lg:top-0 lg:left-0 lg:w-full lg:h-full z-0">
          <Image
            src="/hero_consultancy.png"
            alt="Corporate Operations"
            fill
            sizes="100vw"
            className="object-cover object-center opacity-90"
          />
          <div className="absolute inset-0 bg-[#0d9488]/10 mix-blend-multiply"></div>
        </div>

        {/* Sweeping Curve Container (Bottom on mobile, Right on desktop) */}
        <div
          className="relative w-full lg:absolute lg:right-0 lg:top-0 lg:bottom-0 lg:w-[65%] bg-white z-10 shadow-[-30px_0_50px_rgba(0,0,0,0.1)] flex flex-col justify-center transition-all duration-700 ease-out pt-32 pb-16 px-6 sm:px-12 lg:py-24 lg:px-0 -mt-12 lg:mt-0 [clip-path:ellipse(150%_100%_at_50%_100%)] lg:[clip-path:ellipse(110%_120%_at_100%_50%)]"
        >
          {/* Subtle Anime.js Target SVG Background inside the curve (Now GSAP) */}
          <svg className="promise-line absolute top-0 left-0 w-full h-full pointer-events-none opacity-[0.03]" viewBox="0 0 1000 500" preserveAspectRatio="none">
            <path d="M-100,50 Q250,300 500,50 T1100,50" fill="none" stroke="#0F172A" strokeWidth="4" />
            <path d="M-100,450 Q250,200 500,450 T1100,450" fill="none" stroke="#0F172A" strokeWidth="2" />
          </svg>

          <div className="w-full max-w-[650px] mx-auto lg:ml-auto lg:mx-0 lg:pl-10 lg:pr-24 lg:pl-28 relative z-20">

            <div className="flex flex-col gap-6">

              {/* Pre-Title & Title */}
              <div>
                <span className="text-[#0d9488] font-bold text-[10px] md:text-[11px] tracking-widest uppercase mb-3 block">
                  Discover Compare All – The Future of Consultancy
                </span>
                <h2 className="text-[32px] md:text-[42px] lg:text-[48px] font-extrabold text-gray-900 leading-[1.1] tracking-tight">
                  The Compare All <br />Promise
                </h2>
              </div>

              {/* Description */}
              <p className="text-gray-500 text-[13px] md:text-[14px] leading-relaxed font-normal max-w-lg">
                Whatever your requirement you can rest assured that the most suitable solution will be recommended without any bias to any one manufacturer. Compare All Ltd are delighted to quote on the sourcing and procurement of all your office needs.
              </p>

              {/* Animated Feature Circles (GSAP Magnetic Targets) */}
              <div className="flex flex-wrap items-center gap-6 md:gap-10 mt-6">
                {[
                  { icon: TrendingUp, label: "Analysis" },
                  { icon: Activity, label: "Audit" },
                  { icon: Target, label: "Procurement" },
                  { icon: ShieldCheck, label: "Management" },
                ].map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-3 cursor-pointer group">
                    <div
                      ref={el => { iconRefs.current[idx] = el }}
                      className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-white flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.08)] border border-gray-100 group-hover:shadow-[0_15px_40px_rgba(13,148,136,0.15)] group-hover:border-[#0d9488]/30 transition-all duration-300 shrink-0"
                    >
                      <item.icon className="w-5 h-5 md:w-6 md:h-6 text-[#0d9488] opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300" />
                    </div>
                    <span className="text-[9px] font-bold text-gray-500 tracking-[0.15em] uppercase group-hover:text-[#0d9488] transition-colors">{item.label}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div className="mt-8">
                <Link href="/contact" className="inline-flex items-center gap-3 bg-[#0d9488] hover:bg-teal-700 text-white text-[13px] font-bold px-6 py-3 rounded-full transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5">
                  Speak To An Expert
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <ChevronRight className="w-3 h-3 text-[#0d9488]" />
                  </div>
                </Link>
              </div>

            </div>
          </div>
        </div>

        {/* Floating Intersecting Portrait */}
        <div
          ref={portraitRef}
          className="opacity-0 absolute z-30 top-[200px] left-1/2 -translate-x-1/2 sm:top-[280px] lg:left-[35%] lg:top-[15%] lg:-translate-x-1/2 lg:right-auto"
        >
          <div className="relative w-[140px] h-[140px] sm:w-[180px] sm:h-[180px] lg:w-[280px] lg:h-[280px] rounded-full border-[6px] lg:border-[10px] border-white overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.2)] bg-gray-100">
            <Image src="/consulting_lead.png" alt="Lead Consultant" fill sizes="(max-width: 640px) 140px, (max-width: 1024px) 180px, 280px" className="object-cover object-top" />
          </div>
          {/* Quote Badge Overlapping the Portrait */}
          <div className="absolute -bottom-2 lg:-bottom-4 right-0 lg:right-10 bg-white rounded-xl lg:rounded-2xl shadow-xl px-3 py-2 lg:px-5 lg:py-3 border border-gray-100 flex items-center gap-2 lg:gap-3">
            <div className="w-6 h-6 lg:w-8 lg:h-8 bg-[#E0F2FE] rounded-full flex items-center justify-center shrink-0">
              <Quote className="w-3 h-3 lg:w-4 lg:h-4 text-[#0d9488] fill-[#0d9488]" />
            </div>
            <span className="text-[10px] lg:text-[12px] font-bold text-gray-900 whitespace-nowrap">The Promise</span>
          </div>
        </div>

      </section>

    </div>
  );
}
